import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2, Clock, AlertCircle, ListChecks,
  CalendarCheck, CalendarDays, Umbrella, UserCheck, UserX, AlertTriangle,
} from 'lucide-react';
import { performanceService } from '@/services/performance.service';
import { StatCard } from '@/components/ui/stat-card';
import { StatTile } from '@/components/ui/stat-tile';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// ─── Period helpers ───────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);
const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' },   { value: 4, label: 'April' },
  { value: 5, label: 'May' },     { value: 6, label: 'June' },
  { value: 7, label: 'July' },    { value: 8, label: 'August' },
  { value: 9, label: 'September' },{ value: 10, label: 'October' },
  { value: 11, label: 'November' },{ value: 12, label: 'December' },
];

// ─── Hero weighted bar (white-on-teal) ──────────────────────────────────────────

function HeroBar({ label, score, detail, fill }: { label: string; score: number; detail: string; fill: string }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-white/90">{label}</span>
        <span className="tabular-nums text-white/70">
          <span className="text-white/50">{detail}</span> · <span className="font-semibold text-white">{score}%</span>
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-white/20">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${score}%`, backgroundColor: fill }}
        />
      </div>
    </div>
  );
}

// ─── Main tab ─────────────────────────────────────────────────────────────────

interface Props { userId: string }

export function StaffPerformanceTab({ userId }: Props) {
  const qc = useQueryClient();

  const [periodType, setPeriodType] = useState<'monthly' | 'quarterly' | 'annual'>('monthly');
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));

  const summaryParams = {
    period_type: periodType,
    year,
    ...(periodType === 'monthly'   ? { month }   : {}),
    ...(periodType === 'quarterly' ? { quarter } : {}),
  };

  const { data: summary, isLoading: loadingSummary, isError: errSummary } = useQuery({
    queryKey: ['performance-summary', userId, summaryParams],
    queryFn: () => performanceService.getSummary(userId, summaryParams),
  });

  const periodLabel = periodType === 'monthly'
    ? `${MONTHS[month - 1]?.label} ${year}`
    : periodType === 'quarterly'
    ? `Q${quarter} ${year}`
    : `${year}`;

  return (
    <div className="space-y-4">

      {/* ── Period selector ─────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={periodType} onValueChange={(v) => setPeriodType(v as typeof periodType)}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="quarterly">Quarterly</SelectItem>
            <SelectItem value="annual">Annual</SelectItem>
          </SelectContent>
        </Select>

        {periodType === 'monthly' && (
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTHS.map((m) => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {periodType === 'quarterly' && (
          <Select value={String(quarter)} onValueChange={(v) => setQuarter(Number(v))}>
            <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Q1</SelectItem>
              <SelectItem value="2">Q2</SelectItem>
              <SelectItem value="3">Q3</SelectItem>
              <SelectItem value="4">Q4</SelectItem>
            </SelectContent>
          </Select>
        )}

        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
          <SelectContent>
            {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>

        <span className="ml-1 text-sm text-muted-foreground">Showing: <strong>{periodLabel}</strong></span>
      </div>

      {/* ── Loading ─────────────────────────────────────────────── */}
      {loadingSummary && (
        <div className="space-y-4">
          <div className="h-44 animate-pulse rounded-2xl bg-muted/40" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-xl bg-muted/40" />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="h-48 animate-pulse rounded-xl bg-muted/40" />
            <div className="h-48 animate-pulse rounded-xl bg-muted/40" />
          </div>
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────── */}
      {errSummary && (
        <div className="rounded-xl border bg-rose-50/50 p-6 text-center text-sm text-rose-600">
          Failed to load performance data.
          <button className="ml-2 underline" onClick={() => qc.invalidateQueries({ queryKey: ['performance-summary', userId] })}>
            Retry
          </button>
        </div>
      )}

      {summary && (
        <>
          {/* ── Hero: score ring + weighted breakdown ─────────────── */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-800 via-teal-700 to-teal-600 p-6 text-white shadow-lg">
            <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
            <div className="relative flex flex-col items-center gap-6 sm:flex-row sm:items-center">
              {/* Score ring */}
              <div className="relative flex flex-shrink-0 items-center justify-center" style={{ width: 128, height: 128 }}>
                <svg width="128" height="128" viewBox="0 0 128 128" className="absolute inset-0 -rotate-90">
                  <circle cx="64" cy="64" r="56" fill="none" stroke="white" strokeOpacity="0.2" strokeWidth="11" />
                  <circle
                    cx="64" cy="64" r="56" fill="none" stroke="white" strokeWidth="11"
                    strokeDasharray={2 * Math.PI * 56}
                    strokeDashoffset={2 * Math.PI * 56 * (1 - summary.overall_score / 100)}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 1s ease' }}
                  />
                </svg>
                <div className="relative flex flex-col items-center">
                  <span className="text-4xl font-bold leading-none">{summary.overall_score}</span>
                  <span className="mt-1 text-xs opacity-70">/ 100</span>
                </div>
              </div>

              {/* Label + weighted bars */}
              <div className="min-w-0 flex-1 self-stretch">
                <div className="mb-1 flex flex-wrap items-center gap-3">
                  <h3 className="text-xl font-bold">Overall Performance</h3>
                  <span className="rounded-full bg-white/20 px-3 py-0.5 text-sm font-semibold">
                    {summary.score_label}
                  </span>
                </div>
                <p className="mb-4 text-sm opacity-80">{periodLabel}</p>
                <div className="space-y-3">
                  <HeroBar label="Attendance"      score={summary.attendance.score}
                    detail={`${summary.attendance.present}/${summary.attendance.total} days`} fill="#38bdf8" />
                  <HeroBar label="Task Completion" score={summary.tasks.score}
                    detail={`${summary.tasks.completed}/${summary.tasks.total} tasks`} fill="#c4b5fd" />
                </div>
              </div>
            </div>
          </div>

          {/* ── KPI row ───────────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard label="Attendance" value={`${summary.attendance.score}%`}
              hint={`${summary.attendance.present}/${summary.attendance.total} days`} icon={CalendarCheck} tone="sky" />
            <StatCard label="Task Completion" value={`${summary.tasks.score}%`}
              hint={`${summary.tasks.completed}/${summary.tasks.total} tasks`} icon={ListChecks} tone="violet" />
            <StatCard label="Leave Used" value={`${summary.leave.used_days}d`} icon={Umbrella} tone="amber" />
          </div>

          {/* ── Detail columns ────────────────────────────────────── */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Attendance detail */}
            <Card>
              <SectionHeader icon={<CalendarDays />} title="Attendance Detail" />
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  <StatTile label="Present"  value={summary.attendance.present} icon={UserCheck}     tone="emerald" />
                  <StatTile label="Absent"   value={summary.attendance.absent}  icon={UserX}         tone="red" />
                  <StatTile label="Late"     value={summary.attendance.late}    icon={AlertTriangle} tone="yellow" />
                </div>
              </CardContent>
            </Card>

            {/* Task details */}
            <Card>
              <SectionHeader icon={<ListChecks />} title="Task Details" />
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  <StatTile label="Completed"   value={summary.tasks.completed}   icon={CheckCircle2} tone="emerald" />
                  <StatTile label="In Progress" value={summary.tasks.in_progress} icon={Clock}        tone="sky" />
                  <StatTile label="Overdue"     value={summary.tasks.overdue}     icon={AlertCircle}  tone="rose" />
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

    </div>
  );
}
